import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SchoolIcon from '@mui/icons-material/School';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GppGoodIcon from '@mui/icons-material/GppGood';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SpeedIcon from '@mui/icons-material/Speed';
import { Link } from 'react-router-dom';

const Iso9001: React.FC = () => {
  const implementationProcess = [
    {
      title: "Gap Analysis",
      description: "We assess your current quality management processes against ISO 9001:2015 requirements to identify improvement areas.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "QMS Design",
      description: "We design a Quality Management System tailored to your organization's specific needs, goals, and industry requirements.",
      icon: <TuneIcon fontSize="large" />
    },
    {
      title: "Process Development",
      description: "We develop or refine your processes to align with ISO 9001:2015 requirements while ensuring they add practical value.",
      icon: <BuildIcon fontSize="large" />
    },
    {
      title: "Documentation",
      description: "We create lean, effective documentation for your QMS including policies, procedures, and work instructions.",
      icon: <FactCheckIcon fontSize="large" />
    },
    {
      title: "Implementation",
      description: "We help you implement the QMS throughout your organization with a focus on practical application and value.",
      icon: <AutorenewIcon fontSize="large" />
    },
    {
      title: "Training",
      description: "We train your team on ISO 9001 requirements, internal auditing, and their roles in the QMS.",
      icon: <SchoolIcon fontSize="large" />
    },
    {
      title: "Internal Audit",
      description: "We conduct internal audits to verify compliance and identify opportunities for improvement before certification.",
      icon: <ManageAccountsIcon fontSize="large" />
    },
    {
      title: "Certification Support",
      description: "We guide you through the certification audit process and help address any non-conformities identified.",
      icon: <GppGoodIcon fontSize="large" />
    }
  ];

  const qmsPrinciples = [
    {
      title: "Customer Focus",
      description: "Meeting and exceeding customer expectations to enhance satisfaction and loyalty.",
      icon: <PeopleAltIcon fontSize="large" />
    },
    {
      title: "Leadership",
      description: "Establishing unity of purpose and direction to create conditions for employee engagement.",
      icon: <ManageAccountsIcon fontSize="large" />
    },
    {
      title: "Engagement of People",
      description: "Empowering and engaging people at all levels to enhance capability to create value.",
      icon: <PeopleAltIcon fontSize="large" />
    },
    {
      title: "Process Approach",
      description: "Managing activities as interrelated processes functioning as a coherent system.",
      icon: <TuneIcon fontSize="large" />
    },
    {
      title: "Improvement",
      description: "Focusing on continuous improvement for maintaining performance levels and reacting to changes.",
      icon: <AutorenewIcon fontSize="large" />
    },
    {
      title: "Evidence-based Decision Making",
      description: "Decisions based on the analysis and evaluation of data and information for desired results.",
      icon: <AssessmentIcon fontSize="large" />
    },
    {
      title: "Relationship Management",
      description: "Managing relationships with interested parties to optimize their impact on performance.",
      icon: <PeopleAltIcon fontSize="large" />
    }
  ];

  const benefits = [
    "Improved product and service quality",
    "Enhanced customer satisfaction and loyalty",
    "More efficient processes and reduced waste",
    "Better decision-making based on data and evidence",
    "Reduced costs and improved profitability",
    "Access to new markets that require ISO 9001 certification",
    "Improved organizational culture focused on quality",
    "Enhanced risk management and reduction of costly mistakes",
    "Greater consistency in operations and outputs",
    "Improved supplier relationships and performance"
  ];

  const certificationFAQs = [
    {
      question: "What is the difference between ISO 9001:2015 and ISO 9001:2008?",
      answer: "ISO 9001:2015 is the latest version of the standard, which includes more emphasis on risk-based thinking and customer satisfaction. ISO 9001:2008 is the previous version."
    },
    {
      question: "How long is ISO 9001 certification valid for?",
      answer: "ISO 9001 certification is valid for three years. After this period, a recertification audit is required to continue certification."
    },
    {
      question: "Can ISO 9001 be implemented without certification?",
      answer: "Yes, ISO 9001 can be implemented without certification. Many organizations choose to implement the standard internally for continuous improvement and efficiency."
    },
    {
      question: "What is the cost of ISO 9001 certification?",
      answer: "The cost of ISO 9001 certification can vary depending on the size and complexity of the organization, the scope of certification, and the certification body. We offer competitive pricing and can provide a quote based on your specific needs."
    },
    {
      question: "How often should an organization audit its Quality Management System?",
      answer: "The frequency of audits depends on the organization's risk assessment and the results of previous audits. Generally, audits are conducted at least annually, but more frequent audits may be required based on the organization's risk assessment."
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              ISO 9001:2015 Quality Management
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive implementation and certification services to enhance your organization's quality performance
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is ISO 9001:2015?
              </Typography>
              <Typography paragraph>
                ISO 9001:2015 is the internationally recognized standard for Quality Management Systems (QMS). 
                It provides a framework for organizations to establish consistent processes that ensure the delivery 
                of quality products and services that meet customer and regulatory requirements.
              </Typography>
              <Typography paragraph>
                This standard is based on seven quality management principles and follows a process-based approach with 
                Plan-Do-Check-Act methodology at its core. It emphasizes risk-based thinking, leadership engagement, 
                and continuous improvement.
              </Typography>
              <Typography paragraph>
                ISO 9001 certification demonstrates to customers, suppliers, and stakeholders your commitment to quality 
                and customer satisfaction, providing a competitive advantage in the marketplace.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Quality Management Team"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* ISO 9001 Principles Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Seven Principles of ISO 9001
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            },
            gap: 4 
          }}>
            {qmsPrinciples.map((principle, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#1976d2',
                    }} />
                    <Box sx={{
                      width: 60,
                      height: 60,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#1976d215',
                      color: '#1976d2',
                      mb: 3,
                    }}>
                      {principle.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="#0c2340"
                    >
                      {principle.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {principle.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Implementation Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Our ISO 9001 Implementation Process
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            We follow a structured approach to implement ISO 9001:2015 that ensures effective integration with your business operations
          </Typography>
        </motion.div>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(4, 1fr)' 
          },
          gap: 4 
        }}>
          {implementationProcess.map((step, index) => (
            <Box key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Box sx={{
                    width: 60,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#1976d215',
                    color: '#1976d2',
                    mb: 3,
                  }}>
                    {step.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight="bold"
                    color="#0c2340"
                  >
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Performance Metrics Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6,
            alignItems: 'center' 
          }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Measuring Quality Performance
                </Typography>
                <Typography paragraph>
                  ISO 9001:2015 emphasizes the need for organizations to monitor, measure, analyze, and 
                  evaluate their quality performance. This helps ensure that your Quality Management System 
                  is achieving its intended results and driving continuous improvement.
                </Typography>
                <Typography paragraph>
                  Our implementation services include helping you establish appropriate quality metrics 
                  and performance indicators aligned with your business objectives. We assist in setting up 
                  measurement systems that provide meaningful data for decision-making and improvement.
                </Typography>
                <Typography paragraph>
                  With a robust measurement framework, you can identify trends, anticipate issues before they 
                  become problems, and quantify the benefits of your quality management system to demonstrate 
                  return on investment.
                </Typography>
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Quality Performance Metrics"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 2 }}
          >
            Benefits of ISO 9001 Certification
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
          >
            Implementing ISO 9001 provides numerous benefits that extend beyond just compliance
          </Typography>
        </motion.div>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                }}
              >
                <List>
                  {benefits.slice(0, 5).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                }}
              >
                <List>
                  {benefits.slice(5).map((benefit, index) => (
                    <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Risk-Based Thinking Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 6,
            alignItems: 'center' 
          }}>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Box 
                  component="img"
                  src="https://images.unsplash.com/photo-1507925921958-8a62f3d1a50d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                  alt="Risk-Based Thinking"
                  sx={{
                    width: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <Typography 
                  variant="h4" 
                  component="h2" 
                  gutterBottom 
                  fontWeight="bold"
                  color="#0c2340"
                >
                  Risk-Based Thinking
                </Typography>
                <Typography paragraph>
                  ISO 9001:2015 emphasizes a risk-based approach to quality management, requiring 
                  organizations to identify and address risks and opportunities that could affect their 
                  ability to deliver conforming products and services.
                </Typography>
                <Typography paragraph>
                  Our implementation services help you establish effective risk management processes 
                  that are proportionate to your organization's context and needs. We assist in developing 
                  practical risk assessment methodologies, risk registers, and mitigation strategies.
                </Typography>
                <Typography paragraph>
                  By implementing risk-based thinking, your organization can prevent or reduce undesired 
                  effects, improve operational efficiency, and seize opportunities for improvement and innovation.
                </Typography>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Why Choose RTT for ISO 9001?
              </Typography>
              <Typography paragraph>
                Right Track Technologies Limited offers comprehensive ISO 9001 implementation and certification services
                that help organizations establish, maintain, and continually improve their quality management systems.
              </Typography>
              
              <List>
                {[
                  "Experienced Consultants: Our team includes certified quality management professionals with extensive experience in ISO 9001 implementation across diverse industries.",
                  "Practical Approach: We focus on creating systems that add real value to your business, not just meeting certification requirements.",
                  "Customized Solutions: We tailor ISO 9001 implementation to your specific organization size, industry, and culture.",
                  "Integration: We help integrate ISO 9001 with other management systems like ISO 27001 or ISO 14001 for maximum efficiency.",
                  "Long-term Partnership: We provide ongoing support to help maintain and continually improve your quality management system."
                ].map((point, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Quality Team Meeting"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Client Success Stories - Statistics Section */}
      <Box sx={{ 
        bgcolor: '#f5f7fa', 
        py: 10,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              Our Clients' Success with ISO 9001
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 4 
          }}>
            {[
              {
                statistic: "25%",
                description: "Average reduction in operational costs following ISO 9001 implementation",
                icon: <SpeedIcon fontSize="large" />
              },
              {
                statistic: "35%",
                description: "Average increase in customer satisfaction scores after certification",
                icon: <PeopleAltIcon fontSize="large" />
              },
              {
                statistic: "100%",
                description: "Success rate in helping clients achieve ISO 9001 certification",
                icon: <GppGoodIcon fontSize="large" />
              },
              {
                statistic: "40%",
                description: "Average reduction in product/service defects and non-conformities",
                icon: <CheckCircleOutlineIcon fontSize="large" />
              }
            ].map((item, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: '#1976d215',
                      color: '#1976d2',
                      mb: 3,
                    }}>
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      component="p"
                      gutterBottom
                      fontWeight="bold"
                      color="#1976d2"
                    >
                      {item.statistic}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6,
          alignItems: 'center' 
        }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Ready to Start Your ISO 9001 Journey?
              </Typography>
              <Typography paragraph>
                Implementing ISO 9001:2015 is a strategic decision that can help your organization improve its 
                overall performance and provide a sound basis for sustainable development initiatives.
              </Typography>
              <Typography paragraph>
                At Right Track Technologies Limited, we understand that every organization is unique. That's why we offer
                tailored ISO 9001 implementation services that address your specific needs while ensuring compliance
                with the standard's requirements.
              </Typography>
              <Typography paragraph sx={{ mb: 4 }}>
                Contact us today to learn how we can help you implement an effective quality management system 
                that drives real business value.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="large"
                component={Link}
                to="/contact?service=iso9001"
                sx={{ 
                  mt: 2,
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  fontWeight: 'bold'
                }}
              >
                Request a Free Consultation
              </Button>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="Quality Management Team"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              align="center" 
              sx={{ maxWidth: '800px', mx: 'auto', mb: 6 }}
            >
              Get answers to common questions about ISO 9001 certification
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(1, 1fr)', 
              md: 'repeat(2, 1fr)' 
            },
            gap: 4 
          }}>
            {certificationFAQs.map((faq, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    }}
                  >
                    <Typography variant="h6" gutterBottom fontWeight="bold" color="#0c2340">
                      {faq.question}
                    </Typography>
                    <Typography color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Iso9001; 